import { type ReactNode } from 'react';
import { Dropdown } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { VendorCustomerType } from '@econtractor/types';
import SolarIcon from 'components/icons/SolarIcon';
import SvgEdit from 'components/svg/SvgEdit';
import SvgTrash from 'components/svg/SvgTrash';

interface VendorCustomerActionProps {
    row: VendorCustomerType;
    onEdit: (row: VendorCustomerType) => void;
    onDelete: (row: VendorCustomerType) => void;
}

const VendorCustomerAction = ({ row, onEdit, onDelete }: VendorCustomerActionProps) => {
    const { t } = useTranslation(['Vendors', 'common']);

    const renderMenuItem = (
        icon: ReactNode,
        title: string,
        subtitle: string,
        onSelect: () => void,
        options?: { danger?: boolean },
    ) => (
        <Dropdown.Item
            as="button"
            className={options?.danger ? 'text-danger' : undefined}
            onClick={(e) => {
                onSelect();
                e.currentTarget.blur();
            }}
        >
            <div className="d-flex align-items-start w-100 text-start">
                <span
                    className={`d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon${
                        options?.danger ? ' text-danger' : ''
                    }`}
                >
                    {icon}
                </span>
                <div className="flex-grow-1 min-w-0">
                    <div className={`fw-medium ${options?.danger ? 'text-danger' : ''}`}>{title}</div>
                    <small className="text-muted warehouse-team-menu-hint">{subtitle}</small>
                </div>
            </div>
        </Dropdown.Item>
    );

    const menu = (
        <Dropdown.Menu
            className="p-2 shadow-sm warehouse-team-menu"
            popperConfig={{ strategy: 'fixed' }}
            renderOnMount
        >
            {renderMenuItem(
                <SvgEdit />,
                t('vendorPopup.edit'),
                t('subjects.editHint'),
                () => onEdit(row),
            )}

            <Dropdown.Divider className="my-2" />

            {renderMenuItem(
                <SvgTrash />,
                t('common:buttons.delete'),
                t('subjects.deleteHint'),
                () => onDelete(row),
                { danger: true },
            )}
        </Dropdown.Menu>
    );

    return (
        <Dropdown align="end">
            <Dropdown.Toggle
                className="p-1 py-0"
                size="sm"
                variant="light-secondary"
                id={`dropdown-vendor-customer-${row.ID}`}
            >
                <SolarIcon name="three-dots" />
            </Dropdown.Toggle>

            {typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
        </Dropdown>
    );
};

export default VendorCustomerAction;
